import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from 'react-i18next';
import { AuthenticatedImage } from '@/components/ui/AuthenticatedImage';

type Action = 'CUISSON' | 'REFROIDISSEMENT' | 'MAINTIEN_CHAUD' | 'SURGELATION';

const ACTION_CONFIG: Record<Action, {
  label: string; emoji: string; color: string;
  cardBg: string; badge: string;
  min: number; max: number; defaultTemp: number; hint: string;
  isGood: (t: number) => boolean;
}> = {
  CUISSON: {
    label: 'Cuisson', emoji: '🔥', color: '#EA580C',
    cardBg: 'bg-orange-50 border-orange-200', badge: 'bg-orange-100 text-orange-800',
    min: 0, max: 120, defaultTemp: 25,
    hint: '≥ 63°C recommandé',
    isGood: (t) => t >= 63,
  },
  REFROIDISSEMENT: {
    label: 'Refroidissement', emoji: '❄️', color: '#0284C7',
    cardBg: 'bg-sky-50 border-sky-200', badge: 'bg-sky-100 text-sky-800',
    min: -10, max: 100, defaultTemp: 65,
    hint: '≤ 10°C en moins de 2h',
    isGood: (t) => t <= 10,
  },
  MAINTIEN_CHAUD: {
    label: 'Maintien au chaud', emoji: '♨️', color: '#D97706',
    cardBg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-800',
    min: 40, max: 100, defaultTemp: 70,
    hint: '≥ 63°C recommandé',
    isGood: (t) => t >= 63,
  },
  SURGELATION: {
    label: 'Surgélation', emoji: '🧊', color: '#4F46E5',
    cardBg: 'bg-indigo-50 border-indigo-200', badge: 'bg-indigo-100 text-indigo-800',
    min: -40, max: 5, defaultTemp: -5,
    hint: '≤ -18°C recommandé',
    isGood: (t) => t <= -18,
  },
};

function toLocalDatetimeValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function TempSlider({ value, onChange, min, max, hint, isGood }: {
  value: number; onChange: (v: number) => void;
  min: number; max: number; hint: string;
  isGood: (t: number) => boolean;
}) {
  const good = isGood(value);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500">{hint}</span>
        <span className={`text-2xl font-bold tabular-nums ${good ? 'text-green-600' : 'text-red-600'}`}>
          {value > 0 ? '+' : ''}{value}°C
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={1} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-3 rounded-full appearance-none cursor-pointer"
        style={{ accentColor: good ? '#16a34a' : '#dc2626' }}
      />
      <div className="flex justify-between text-xs text-neutral-400">
        <span>{min}°C</span>
        <span>{max}°C</span>
      </div>
    </div>
  );
}

type Step = 1 | 2 | 3;

export default function TempTrackingPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const qc = useQueryClient();

  // List
  const { data: records = [], isLoading } = useQuery({
    queryKey: ['temp-trackings'],
    queryFn: () => apiClient.listTempTrackings(),
  });

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [action, setAction] = useState<Action | null>(null);
  const [productName, setProductName] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [startTime, setStartTime] = useState(toLocalDatetimeValue(new Date()));
  const [startTemp, setStartTemp] = useState(25);
  const [hasEnd, setHasEnd] = useState(false);
  const [endTime, setEndTime] = useState(toLocalDatetimeValue(new Date()));
  const [endTemp, setEndTemp] = useState(25);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  // Complete tracking (add end to existing)
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [cEndTime, setCEndTime] = useState(toLocalDatetimeValue(new Date()));
  const [cEndTemp, setCEndTemp] = useState(25);

  const cfg = action ? ACTION_CONFIG[action] : null;

  const createMutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('action', action!);
      if (productName) fd.append('productName', productName);
      fd.append('startTime', new Date(startTime).toISOString());
      fd.append('startTemp', String(startTemp));
      if (hasEnd) {
        fd.append('endTime', new Date(endTime).toISOString());
        fd.append('endTemp', String(endTemp));
      }
      if (photo) fd.append('photo', photo);
      return apiClient.createTempTracking(fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['temp-trackings'] });
      resetWizard();
    },
  });

  const completeMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiClient.updateTempTracking(id, {
        endTime: new Date(cEndTime).toISOString(),
        endTemp: cEndTemp,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['temp-trackings'] });
      setCompletingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteTempTracking(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['temp-trackings'] }),
  });

  function resetWizard() {
    setShowWizard(false);
    setStep(1);
    setAction(null);
    setProductName('');
    setPhoto(null);
    setPhotoPreview(null);
    setHasEnd(false);
    setStartTime(toLocalDatetimeValue(new Date()));
    setStartTemp(25);
    setEndTime(toLocalDatetimeValue(new Date()));
    setEndTemp(25);
  }

  function handlePhotoChange(file: File) {
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  function selectAction(a: Action) {
    setAction(a);
    const c = ACTION_CONFIG[a];
    setStartTemp(c.defaultTemp);
    setEndTemp(c.defaultTemp);
    setStep(2);
  }

  function fmt(dateStr: string) {
    return new Date(dateStr).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">{t('tempTracking.title')}</h1>
          <p className="text-sm text-neutral-500 mt-0.5">{t('tempTracking.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm shadow-md transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('tempTracking.add')}
        </button>
      </div>

      {/* Records list */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-28 bg-neutral-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-16 text-neutral-400">
          <div className="text-5xl mb-3">🌡️</div>
          <p className="font-medium">{t('tempTracking.noRecords')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map((r: any) => {
            const c = ACTION_CONFIG[r.action as Action];
            const inProgress = !r.endTime;
            return (
              <div key={r.id} className={`rounded-xl border p-4 ${c.cardBg}`}>
                <div className="flex items-start gap-3">
                  {/* Photo */}
                  {r.photoUrl && (
                    <AuthenticatedImage
                      src={r.photoUrl}
                      alt={r.productName || ''}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    {/* Action badge + product name */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.badge}`}>
                        {c.emoji} {c.label}
                      </span>
                      {inProgress ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 animate-pulse">
                          {t('tempTracking.inProgress')}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          {t('tempTracking.completed')}
                        </span>
                      )}
                    </div>
                    {r.productName && (
                      <p className="font-semibold text-neutral-800 truncate">{r.productName}</p>
                    )}
                    {/* Temps */}
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div className="bg-white/70 rounded-lg p-2">
                        <p className="text-xs text-neutral-500 font-medium">{t('tempTracking.start')}</p>
                        <p className="text-xs text-neutral-700">{fmt(r.startTime)}</p>
                        <p className={`text-sm font-bold ${c.isGood(r.startTemp) ? 'text-green-600' : 'text-red-600'}`}>
                          {r.startTemp > 0 ? '+' : ''}{r.startTemp}°C
                        </p>
                      </div>
                      {r.endTime ? (
                        <div className="bg-white/70 rounded-lg p-2">
                          <p className="text-xs text-neutral-500 font-medium">{t('tempTracking.end')}</p>
                          <p className="text-xs text-neutral-700">{fmt(r.endTime)}</p>
                          <p className={`text-sm font-bold ${c.isGood(r.endTemp) ? 'text-green-600' : 'text-red-600'}`}>
                            {r.endTemp > 0 ? '+' : ''}{r.endTemp}°C
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => {
                              setCompletingId(r.id);
                              setCEndTime(toLocalDatetimeValue(new Date()));
                              setCEndTemp(c.defaultTemp);
                            }}
                            className="px-3 py-2 bg-white border-2 border-dashed rounded-lg text-xs font-semibold text-neutral-600 hover:border-orange-400 hover:text-orange-600 transition-colors w-full"
                          >
                            + {t('tempTracking.complete')}
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Footer */}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-neutral-400">
                        {t('tempTracking.by')} {r.createdBy?.displayName || r.createdBy?.email}
                      </p>
                      <button
                        onClick={() => { if (confirm(t('tempTracking.deleteConfirm'))) deleteMutation.mutate(r.id); }}
                        className="text-xs text-red-400 hover:text-red-600 transition-colors"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Complete inline form */}
                {completingId === r.id && (
                  <div className="mt-4 pt-4 border-t border-white/50 space-y-3">
                    <p className="text-sm font-semibold text-neutral-700">{t('tempTracking.completeTitle')}</p>
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">{t('tempTracking.endTime')}</label>
                      <input
                        type="datetime-local"
                        value={cEndTime}
                        onChange={(e) => setCEndTime(e.target.value)}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <TempSlider
                      value={cEndTemp}
                      onChange={setCEndTemp}
                      min={c.min} max={c.max}
                      hint={c.hint} isGood={c.isGood}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCompletingId(null)}
                        className="flex-1 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-600"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={() => completeMutation.mutate({ id: r.id })}
                        disabled={completeMutation.isPending}
                        className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-60"
                      >
                        {completeMutation.isPending ? t('common.saving') : t('common.save')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { if (e.target.files?.[0]) handlePhotoChange(e.target.files[0]); }} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { if (e.target.files?.[0]) handlePhotoChange(e.target.files[0]); }} />

      {/* Wizard modal — portalled to body so fixed positioning isn't clipped by the scroll container */}
      {showWizard && createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
            {/* Modal header — flex-shrink-0 so it never scrolls away */}
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-2">
                {step > 1 && (
                  <button onClick={() => setStep((s) => (s - 1) as Step)} className="p-1 rounded-full hover:bg-neutral-100">
                    <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <h2 className="font-bold text-neutral-900">
                  {step === 1 && t('tempTracking.stepAction')}
                  {step === 2 && t('tempTracking.stepProduct')}
                  {step === 3 && t('tempTracking.stepTemps')}
                </h2>
              </div>
              <button onClick={resetWizard} className="p-1 rounded-full hover:bg-neutral-100">
                <svg className="w-5 h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Step indicators — flex-shrink-0 */}
            <div className="flex-shrink-0 flex items-center gap-1.5 px-5 pt-3 pb-1">
              {([1, 2, 3] as Step[]).map((s) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? 'bg-orange-500' : 'bg-neutral-200'}`} />
              ))}
            </div>

            {/* Scrollable content area */}
            <div className="overflow-y-auto flex-1 overscroll-contain">
            <div className="p-5 space-y-4">
              {/* STEP 1 — Action selection */}
              {step === 1 && (
                <div className="grid grid-cols-2 gap-3">
                  {(Object.entries(ACTION_CONFIG) as [Action, typeof ACTION_CONFIG[Action]][]).map(([key, c]) => (
                    <button
                      key={key}
                      onClick={() => selectAction(key)}
                      className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 border-neutral-200 hover:border-orange-400 hover:bg-orange-50 transition-all active:scale-95"
                    >
                      <span className="text-4xl">{c.emoji}</span>
                      <span className="font-semibold text-sm text-neutral-800 text-center">{c.label}</span>
                      <span className="text-xs text-neutral-400 text-center">{c.hint}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* STEP 2 — Product */}
              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">
                      {t('tempTracking.productName')}
                    </label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder={t('tempTracking.productNamePlaceholder')}
                      className="w-full border border-neutral-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      {t('tempTracking.photo')} <span className="font-normal text-neutral-400">({t('common.optional', 'optionnel')})</span>
                    </label>
                    {photoPreview ? (
                      <div className="relative">
                        <img src={photoPreview} alt="" className="w-full h-40 object-cover rounded-xl" />
                        <button
                          onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                          className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm"
                        >✕</button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => cameraRef.current?.click()}
                          className="flex items-center justify-center gap-2 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {t('tempTracking.takePhoto')}
                        </button>
                        <button
                          onClick={() => galleryRef.current?.click()}
                          className="flex items-center justify-center gap-2 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-xl text-sm font-medium transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t('tempTracking.choosePhoto')}
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setStep(3)}
                    className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold transition-colors"
                  >
                    {t('common.next', 'Suivant')} →
                  </button>
                </div>
              )}

              {/* STEP 3 — Temperatures */}
              {step === 3 && cfg && (
                <div className="space-y-4">
                  {/* Start box */}
                  <div className="border-2 border-neutral-200 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <h3 className="font-semibold text-neutral-800">{t('tempTracking.start')}</h3>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-500 mb-1">{t('tempTracking.time')}</label>
                      <input
                        type="datetime-local"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-500 mb-2">{t('tempTracking.temperature')}</label>
                      <TempSlider
                        value={startTemp} onChange={setStartTemp}
                        min={cfg.min} max={cfg.max}
                        hint={cfg.hint} isGood={cfg.isGood}
                      />
                    </div>
                  </div>

                  {/* End box */}
                  <div className={`border-2 rounded-2xl p-4 space-y-3 transition-colors ${hasEnd ? 'border-neutral-200' : 'border-dashed border-neutral-300'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${hasEnd ? 'bg-red-500' : 'bg-neutral-300'}`} />
                        <h3 className="font-semibold text-neutral-800">{t('tempTracking.end')}</h3>
                      </div>
                      <button
                        onClick={() => setHasEnd(!hasEnd)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hasEnd ? 'bg-orange-500' : 'bg-neutral-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hasEnd ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    {hasEnd && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-neutral-500 mb-1">{t('tempTracking.time')}</label>
                          <input
                            type="datetime-local"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-neutral-500 mb-2">{t('tempTracking.temperature')}</label>
                          <TempSlider
                            value={endTemp} onChange={setEndTemp}
                            min={cfg.min} max={cfg.max}
                            hint={cfg.hint} isGood={cfg.isGood}
                          />
                        </div>
                      </>
                    )}
                    {!hasEnd && (
                      <p className="text-xs text-neutral-400">{t('tempTracking.endHint')}</p>
                    )}
                  </div>

                  <button
                    onClick={() => createMutation.mutate()}
                    disabled={createMutation.isPending}
                    className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-base shadow-md transition-colors disabled:opacity-60"
                  >
                    {createMutation.isPending ? t('tempTracking.submitting') : t('tempTracking.submit')}
                  </button>

                  {createMutation.isError && (
                    <p className="text-sm text-red-500 text-center">{t('common.error')}</p>
                  )}
                </div>
              )}
            </div>
            </div>{/* end scrollable content */}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
